/**
 * ImproveCommentRow — single comment node + (optional) one level of replies.
 *
 * NOTE: keeps the reply rendering NON-recursive (we only support one level
 * of replies anyway) so the visual-edits Babel plugin doesn't choke on a
 * self-referencing JSX node.
 */
import React, { useState } from 'react';
import { Heart, CornerDownRight, Loader2, Send } from 'lucide-react';

const fmtRel = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const RoleBadge = ({ role }) => {
  if (role === 'admin') {
    return <span className="text-[9px] uppercase font-bold text-red-300 bg-red-600/20 border border-red-500/30 rounded-full px-1.5 py-0.5">ADMIN</span>;
  }
  if (role === 'writer') {
    return <span className="text-[9px] uppercase font-bold text-purple-300 bg-purple-600/20 border border-purple-500/30 rounded-full px-1.5 py-0.5">WRITER</span>;
  }
  return null;
};

const CommentBody = ({ c, depth, onLike, onReply, replying }) => (
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 flex-wrap text-[11px]">
      <span className="font-semibold text-indigo-200 truncate">{c.author?.name || 'Anonymous'}</span>
      <RoleBadge role={c.author?.role} />
      <span className="text-slate-500">· {fmtRel(c.created_at)}</span>
      {c.episode?.number != null && depth === 0 && (
        <span className="text-[10px] text-amber-300/80 bg-amber-500/10 border border-amber-400/30 rounded-full px-2 py-0.5">
          Ep {c.episode.number}
        </span>
      )}
    </div>
    <p className="text-sm text-slate-100 whitespace-pre-wrap break-words mt-1">{c.body}</p>
    <div className="flex items-center gap-4 mt-1.5 text-[11px]">
      <button
        type="button"
        onClick={() => onLike(c)}
        className={`inline-flex items-center gap-1 hover:text-pink-300 ${c.liked_by_me ? 'text-pink-400 font-semibold' : 'text-slate-400'}`}
        data-testid={`improve-comment-like-${c.comment_id}`}
      >
        <Heart className={`w-3.5 h-3.5 ${c.liked_by_me ? 'fill-current' : ''}`} />
        <span className="uppercase tracking-wider font-bold">Like</span>
        {c.like_count > 0 && <span className="tabular-nums">· {c.like_count}</span>}
      </button>
      {depth === 0 && (
        <button
          type="button"
          onClick={() => onReply(c)}
          className="inline-flex items-center gap-1 text-slate-400 hover:text-indigo-300"
          data-testid={`improve-comment-reply-${c.comment_id}`}
        >
          <CornerDownRight className="w-3.5 h-3.5" />
          <span className="uppercase tracking-wider font-bold">{replying ? 'Cancel' : 'Reply'}</span>
        </button>
      )}
    </div>
  </div>
);

const Avatar = ({ author }) => {
  if (author?.picture) {
    return <img src={author.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />;
  }
  return <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shrink-0" />;
};

const ImproveCommentRow = ({ c, onLike, onReply, replyingTo, onSubmitReply, busyReplyId }) => {
  const [draft, setDraft] = useState('');
  const replying = replyingTo === c.comment_id;
  const submitReply = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim()) return;
    await onSubmitReply(c, draft.trim());
    setDraft('');
  };
  const replies = c.replies || [];
  return (
    <article className="py-3" data-testid={`improve-comment-${c.comment_id}`}>
      <div className="flex items-start gap-2.5">
        <Avatar author={c.author} />
        <CommentBody c={c} depth={0} onLike={onLike} onReply={onReply} replying={replying} />
      </div>

      {replying && (
        <form onSubmit={submitReply} className="ml-9 sm:ml-12 mt-2 flex items-start gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
            rows={1}
            autoFocus
            placeholder={`Reply to ${c.author?.name || 'comment'}…`}
            className="flex-1 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-y"
            data-testid={`improve-reply-input-${c.comment_id}`}
          />
          <button
            type="submit"
            disabled={!draft.trim() || busyReplyId === c.comment_id}
            className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 text-white"
            data-testid={`improve-reply-submit-${c.comment_id}`}
          >
            {busyReplyId === c.comment_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      )}

      {replies.length > 0 && (
        <ul className="ml-7 sm:ml-10 mt-2 pl-3 border-l-2 border-slate-800 space-y-3">
          {replies.map((r) => (
            <li key={r.comment_id} data-testid={`improve-comment-${r.comment_id}`}>
              <div className="flex items-start gap-2.5">
                <Avatar author={r.author} />
                <CommentBody c={r} depth={1} onLike={onLike} onReply={onReply} replying={false} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

export default ImproveCommentRow;
